import React, { useState, useMemo, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, gql, useMutation } from '@apollo/client';
import {
    Header, Button, Input, TextArea, Form,
    Divider, Label, Message, Loader, Container, Icon
} from 'semantic-ui-react';

import { MediaTile } from '../../../components/MediaTile';
import { PosterAvatar } from '../../../components/PosterAvatar';
import { parseApiDate } from '../../../utils/date';
import { POST_TITLE_LIMIT, POST_DESCRIPTION_LIMIT, characterCountColour } from '../../../constants';

const COMMUNITY_GROUP_ID = 2;

// Post is a union (CommunityUpdate | CommunityPrayerRequest | LeadershipUpdate
// | LeadershipPrayerRequest | Birthday); fields must be selected per member.
const POST_REVIEW_FIELDS = `
    id
    title
    description
    type
    publishAt
    tags { id name groupId }
    poster { name photoUrl }
    images { id url contentType durationSeconds tags { id name groupId } }
`;

const GET_POST_FOR_REVIEW = gql`
     query Post($postId: Int!) {
         post(id: $postId) {
             ... on CommunityUpdate { ${POST_REVIEW_FIELDS} }
             ... on CommunityPrayerRequest { ${POST_REVIEW_FIELDS} }
             ... on LeadershipUpdate { ${POST_REVIEW_FIELDS} }
             ... on LeadershipPrayerRequest { ${POST_REVIEW_FIELDS} }
             ... on Birthday { ${POST_REVIEW_FIELDS} }
         }
     }
`;

const UPDATE_POST_STATUS = gql`
    mutation UpdatePostStatus($updatePostStatusId: Int!, $status: String!, $title: String, $description: String) {
        updatePostStatus(id: $updatePostStatusId, status: $status, title: $title, description: $description) {
            ... on CommunityUpdate { id }
            ... on CommunityPrayerRequest { id }
            ... on LeadershipUpdate { id }
            ... on LeadershipPrayerRequest { id }
            ... on Birthday { id }
        }
    }
`;

type EditableFields = { title: string; description: string };

// --- Component ---

const PostReviewView: React.FC = () => {
    const { postId } = useParams<{ postId: string }>();
    const navigate = useNavigate();
    const [isExiting, setIsExiting] = useState(false);

    const { data, loading, error } = useQuery(GET_POST_FOR_REVIEW, {
        variables: { postId: Number(postId) },
        skip: !postId
    });

    // Seeded once from the query result, then edited locally until the reviewer acts.
    const [form, setForm] = useState<EditableFields | null>(null);
    useEffect(() => {
        if (data?.post && form == null) {
            setForm({
                title: data.post.title || '',
                description: data.post.description || '',
            });
        }
    }, [data, form]);

    const [updateStatus, { loading: updating }] = useMutation(UPDATE_POST_STATUS);

    /**
     * Memoized logic to categorize tags and validate image tagging consistency
     */
    const { communities, postTags, warnings } = useMemo(() => {
        if (!data?.post) return { communities: [], postTags: [], warnings: [] };

        const post = data.post;
        const comms = post.tags.filter((t: any) => t.groupId === COMMUNITY_GROUP_ID);
        const tags = post.tags.filter((t: any) => t.groupId !== COMMUNITY_GROUP_ID);

        const validationWarnings: string[] = [];

        post.images.forEach((img: any, index: number) => {
            const imgGroupIds = new Set(img.tags.map((t: any) => t.groupId as number));

            comms.forEach((c: any) => {
                if (!imgGroupIds.has(c.groupId as number)) {
                    validationWarnings.push(`Image #${index + 1} is not tagged for "${c.name}".`);
                }
            });
        });

        return { communities: comms, postTags: tags, warnings: validationWarnings };
    }, [data]);

    const handleAction = async (status: 'APPROVED' | 'REJECTED') => {
        if (!form) return;
        try {
            await updateStatus({
                variables: {
                    updatePostStatusId: Number(postId),
                    status,
                    title: form.title,
                    description: form.description
                }
            });
            setIsExiting(true);
            setTimeout(() => navigate('/posts/reviews'), 500);
        } catch (err) {
            console.error("Review action failed", err);
        }
    };

    if (loading || !form) return <Loader active inline='centered' style={{ marginTop: '5em' }}>Loading Post...</Loader>;
    if (error || !data?.post) return <Message error content="Could not load post details." />;

    const { post } = data;
    const overLimit = form.title.length > POST_TITLE_LIMIT || form.description.length > POST_DESCRIPTION_LIMIT;

    return (
        <Container
            text
            style={{
                padding: '3em 0em',
                transition: 'opacity 0.5s ease-in-out',
                opacity: isExiting ? 0 : 1
            }}
        >
            <Label basic color="grey" size="small" style={{ marginBottom: '1em' }}>{post.type}</Label>

            {/* Communities Display */}
            {communities.length > 0 && (
                <div style={{ marginBottom: '10px' }}>
                    <span style={{ marginRight: '10px', fontWeight: 'bold', color: '#666' }}>Communities:</span>
                    {communities.map((c: any) => (
                        <Label key={c.id} color="purple" size="small" style={{ marginBottom: '5px' }}>
                            <Icon name="users" /> {c.name}
                        </Label>
                    ))}
                </div>
            )}

            {/* Post Standard Tags */}
            {postTags.length > 0 && (
                <div style={{ marginBottom: '1.5em' }}>
                    {postTags.map((t: any) => (
                        <Label key={t.id} basic color="teal" size="tiny" style={{ marginRight: '5px' }}>
                            #{t.name}
                        </Label>
                    ))}
                </div>
            )}

            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '2em', color: '#666' }}>
                <PosterAvatar photoUrl={post.poster.photoUrl} size={28} />
                <span style={{ marginLeft: '0.8em' }}>
                    <strong>{post.poster.name}</strong> • {parseApiDate(post.publishAt)?.toLocaleString()}
                </span>
            </div>

            {/* Warning Message Box */}
            {warnings.length > 0 && (
                <Message warning icon>
                    <Icon name="warning sign" />
                    <Message.Content>
                        <Message.Header>Tag Mismatch Detected</Message.Header>
                        <ul style={{ marginTop: '5px' }}>
                            {warnings.map((w, i) => (
                                <li key={i}>{w} Are you sure you want to send this post out?</li>
                            ))}
                        </ul>
                    </Message.Content>
                </Message>
            )}

            {/* Image Gallery with Conditional Red Border */}
            {post.images.length > 0 && (
                <div style={{
                    display: 'flex',
                    overflowX: 'auto',
                    gap: '12px',
                    paddingBottom: '15px',
                    marginBottom: '2em'
                }}>
                    {post.images.map((img: any, index: number) => {
                        const postGroupIds: number[] = communities.map((c: any) => c.groupId);
                        const imgGroupIds: number[] = img.tags.map((t: any) => t.groupId);

                        // Explicitly typing 'id' as number to fix the implicit any error
                        const isMissingTags = postGroupIds.some((id: number) => !imgGroupIds.includes(id));

                        return (
                            <div key={img.id} style={{ position: 'relative', flex: '0 0 auto' }}>
                                <MediaTile
                                    url={img.url}
                                    contentType={img.contentType}
                                    durationSeconds={img.durationSeconds}
                                    rounded
                                    bordered
                                    controls
                                    style={{
                                        height: '220px',
                                        objectFit: 'cover',
                                        border: isMissingTags ? '3px solid #db2828' : '1px solid #ddd'
                                    }}
                                />
                                {isMissingTags && (
                                    <Label color="red" corner="left" icon="warning" />
                                )}
                            </div>
                        );
                    })}
                </div>
            )}

            <Form>
                <Form.Field required>
                    <label>Title</label>
                    <Input
                        value={form.title}
                        disabled={updating || isExiting}
                        onChange={(_, { value }) => setForm(f => f && ({ ...f, title: value }))}
                    />
                    <div style={{ textAlign: 'right', fontSize: '0.8em', color: characterCountColour(form.title.length, POST_TITLE_LIMIT) }}>
                        {form.title.length} / {POST_TITLE_LIMIT}
                    </div>
                </Form.Field>

                <Form.Field required>
                    <label>Description</label>
                    <TextArea
                        rows={6}
                        value={form.description}
                        disabled={updating || isExiting}
                        onChange={(_, { value }) => setForm(f => f && ({ ...f, description: value as string }))}
                    />
                    <div style={{ textAlign: 'right', fontSize: '0.8em', color: characterCountColour(form.description.length, POST_DESCRIPTION_LIMIT) }}>
                        {form.description.length} / {POST_DESCRIPTION_LIMIT}
                    </div>
                </Form.Field>
            </Form>

            <Divider style={{ margin: '4em 0 2em 0' }} />

            <div style={{ display: 'flex', gap: '20px' }}>
                <Button
                    basic
                    color="red"
                    size="huge"
                    fluid
                    icon="trash"
                    labelPosition="left"
                    content="Delete"
                    loading={updating}
                    disabled={isExiting}
                    onClick={() => handleAction('REJECTED')}
                />
                <Button
                    positive
                    size="huge"
                    fluid
                    icon="checkmark"
                    labelPosition="left"
                    content={warnings.length > 0 ? "Approve Anyway" : "Approve"}
                    loading={updating}
                    disabled={isExiting || overLimit || !form.title || !form.description}
                    onClick={() => handleAction('APPROVED')}
                />
            </div>
        </Container>
    );
};

export { PostReviewView };
